// Pre-baked Voronoi fracture patterns for the cell-fracture system.
//
// Built once at module load. 5 impact zones × 3 intensities × 4 variants = 60
// patterns; each pattern tiles the unit brick rect [0,1]² with convex cells.
// At break time we look up a pattern by (zone, intensity, variant) and spawn
// one chunk per cell — no polygon math at runtime.

// Impact zones: center, left, right, top, bottom. Intensity: low/mid/high.
// Kept as plain numbers — the runtime check in `impactZoneOf` enforces the
// valid range and downstream uses only index typed arrays.
export type ImpactZone = number;
export type Intensity = number;

export const ZONE_CENTER = 0;
export const ZONE_LEFT = 1;
export const ZONE_RIGHT = 2;
export const ZONE_TOP = 3;
export const ZONE_BOTTOM = 4;

export const INTENSITY_LOW = 0;
export const INTENSITY_MID = 1;
export const INTENSITY_HIGH = 2;

export const VARIANTS_PER_BUCKET = 4;
const ZONE_COUNT = 5;
const INTENSITY_COUNT = 3;
const PATTERN_COUNT = ZONE_COUNT * INTENSITY_COUNT * VARIANTS_PER_BUCKET;

/** Per-cell baked geometry. Vertex coords are centroid-relative (zero-centered). */
export interface Cell {
  readonly verts: Float32Array; // [x0,y0,x1,y1,...] in unit space, relative to centroid
  readonly vertCount: number;
  readonly cx: number;          // centroid in unit space (0..1)
  readonly cy: number;
  readonly area: number;        // 0..1
  readonly brightnessKey: number; // 0..1, higher near impact
  readonly path: Path2D;        // pre-built Path2D, centroid-relative
}

export interface Pattern {
  readonly cells: readonly Cell[];
  /** Indices into `cells`, sorted by brightnessKey descending. */
  readonly indexByBrightness: Uint16Array;
}

export function patternIndex(zone: ImpactZone, intensity: Intensity, variant: number): number {
  const v = variant & (VARIANTS_PER_BUCKET - 1);
  return (zone * INTENSITY_COUNT + intensity) * VARIANTS_PER_BUCKET + v;
}

/** Classify an in-brick impact point (0..1 local coords) into a zone bucket. */
export function impactZoneOf(localX: number, localY: number): ImpactZone {
  const dxC = localX - 0.5;
  const dyC = localY - 0.5;
  // Center band — within ~0.25 of brick center on both axes.
  if (Math.abs(dxC) < 0.22 && Math.abs(dyC) < 0.22) return ZONE_CENTER;
  // Otherwise: snap to the closest edge by distance to that edge.
  const dLeft = localX;
  const dRight = 1 - localX;
  const dTop = localY;
  const dBot = 1 - localY;
  const minD = Math.min(dLeft, dRight, dTop, dBot);
  if (minD === dLeft) return ZONE_LEFT;
  if (minD === dRight) return ZONE_RIGHT;
  if (minD === dTop) return ZONE_TOP;
  return ZONE_BOTTOM;
}

// ---- Deterministic RNG (mulberry32) -----------------------------------------

function makeRng(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---- Convex polygon utilities -----------------------------------------------

type Poly = number[]; // flat [x0,y0,x1,y1,...]

function polygonArea(p: Poly): number {
  let a = 0;
  const n = p.length >> 1;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = p[i * 2] as number;
    const yi = p[i * 2 + 1] as number;
    const xj = p[j * 2] as number;
    const yj = p[j * 2 + 1] as number;
    a += xi * yj - xj * yi;
  }
  return a * 0.5;
}

function polygonCentroid(p: Poly): [number, number] {
  const n = p.length >> 1;
  let cx = 0;
  let cy = 0;
  let a = 0;
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = p[i * 2] as number;
    const yi = p[i * 2 + 1] as number;
    const xj = p[j * 2] as number;
    const yj = p[j * 2 + 1] as number;
    const cross = xi * yj - xj * yi;
    cx += (xi + xj) * cross;
    cy += (yi + yj) * cross;
    a += cross;
  }
  if (Math.abs(a) < 1e-9) {
    // Degenerate — fall back to vertex mean.
    let mx = 0; let my = 0;
    for (let i = 0; i < n; i++) {
      mx += p[i * 2] as number;
      my += p[i * 2 + 1] as number;
    }
    return [mx / n, my / n];
  }
  return [cx / (3 * a), cy / (3 * a)];
}

/**
 * Clip convex polygon by half-plane `ax + by <= c`. Sutherland-Hodgman.
 * Returns a new polygon or empty array when fully clipped.
 */
function clipHalfPlane(poly: Poly, a: number, b: number, c: number): Poly {
  const n = poly.length >> 1;
  if (n === 0) return [];
  const out: Poly = [];
  for (let i = 0; i < n; i++) {
    const j = (i + 1) % n;
    const xi = poly[i * 2] as number;
    const yi = poly[i * 2 + 1] as number;
    const xj = poly[j * 2] as number;
    const yj = poly[j * 2 + 1] as number;
    const di = a * xi + b * yi - c;
    const dj = a * xj + b * yj - c;
    const insideI = di <= 1e-9;
    const insideJ = dj <= 1e-9;
    if (insideI) out.push(xi, yi);
    if (insideI !== insideJ) {
      // Edge crosses boundary — emit intersection.
      const t = di / (di - dj);
      out.push(xi + (xj - xi) * t, yi + (yj - yi) * t);
    }
  }
  return out;
}

// ---- Site seeding -----------------------------------------------------------

interface Site { x: number; y: number }

function seedSites(zone: ImpactZone, intensity: Intensity, rng: () => number): Site[] {
  let gridCols: number;
  let gridRows: number;
  let extras: number;
  if (intensity === INTENSITY_LOW) {
    gridCols = 3; gridRows = 2; extras = 2;
  } else if (intensity === INTENSITY_MID) {
    gridCols = 4; gridRows = 3; extras = 2;
  } else {
    gridCols = 4; gridRows = 4; extras = 4;
  }
  const sites: Site[] = [];
  // Stratified grid with per-cell jitter.
  for (let r = 0; r < gridRows; r++) {
    for (let c = 0; c < gridCols; c++) {
      const x = (c + 0.5) / gridCols + (rng() - 0.5) * (0.7 / gridCols);
      const y = (r + 0.5) / gridRows + (rng() - 0.5) * (0.7 / gridRows);
      sites.push({ x: clamp01(x), y: clamp01(y) });
    }
  }
  // Bias extras toward impact zone — clustered within ~0.18 radius.
  const [ix, iy] = impactAnchor(zone);
  for (let i = 0; i < extras; i++) {
    const a = rng() * Math.PI * 2;
    const r = rng() * 0.18;
    sites.push({
      x: clamp01(ix + Math.cos(a) * r),
      y: clamp01(iy + Math.sin(a) * r),
    });
  }
  // De-duplicate near-coincident sites — Voronoi degenerates when two sites
  // are too close (slivers + numerical noise).
  return dedupeSites(sites, 0.04);
}

function impactAnchor(zone: ImpactZone): [number, number] {
  switch (zone) {
    case ZONE_LEFT: return [0.05, 0.5];
    case ZONE_RIGHT: return [0.95, 0.5];
    case ZONE_TOP: return [0.5, 0.05];
    case ZONE_BOTTOM: return [0.5, 0.95];
    default: return [0.5, 0.5];
  }
}

function dedupeSites(sites: Site[], minDist: number): Site[] {
  const out: Site[] = [];
  const m2 = minDist * minDist;
  for (const s of sites) {
    let ok = true;
    for (const o of out) {
      const dx = s.x - o.x;
      const dy = s.y - o.y;
      if (dx * dx + dy * dy < m2) { ok = false; break; }
    }
    if (ok) out.push(s);
  }
  return out;
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

// ---- Voronoi cell construction ---------------------------------------------

const UNIT_RECT: Poly = [0, 0, 1, 0, 1, 1, 0, 1];

function voronoiCells(sites: Site[]): Poly[] {
  const cells: Poly[] = [];
  for (let i = 0; i < sites.length; i++) {
    const si = sites[i] as Site;
    let poly: Poly = UNIT_RECT.slice();
    for (let j = 0; j < sites.length; j++) {
      if (i === j) continue;
      const sj = sites[j] as Site;
      // Keep p where |p-si| <= |p-sj|:
      //   2*(sj - si) · p <= |sj|² - |si|²
      const a = 2 * (sj.x - si.x);
      const b = 2 * (sj.y - si.y);
      const c = (sj.x * sj.x + sj.y * sj.y) - (si.x * si.x + si.y * si.y);
      poly = clipHalfPlane(poly, a, b, c);
      if (poly.length === 0) break;
    }
    cells.push(poly);
  }
  return cells;
}

// ---- Pattern build ----------------------------------------------------------

function buildPattern(zone: ImpactZone, intensity: Intensity, variant: number): Pattern {
  // Stable per-bucket seed — same atlas every load.
  const seed = 0x9e3779b1 ^ (zone * 73) ^ (intensity * 419) ^ (variant * 8191);
  const rng = makeRng(seed);
  const sites = seedSites(zone, intensity, rng);
  const polys = voronoiCells(sites);
  const [ix, iy] = impactAnchor(zone);
  const cells: Cell[] = [];
  let maxDist = 0;
  // First pass: build cell skeletons (no brightnessKey yet).
  interface Skeleton {
    verts: Float32Array;
    vertCount: number;
    cx: number;
    cy: number;
    area: number;
    distToImpact: number;
  }
  const skels: Skeleton[] = [];
  for (const poly of polys) {
    if (poly.length < 6) continue; // need at least 3 vertices
    const area = Math.abs(polygonArea(poly));
    if (area < 0.005) continue; // discard slivers
    const [cx, cy] = polygonCentroid(poly);
    const vertCount = poly.length >> 1;
    // Store vertices centroid-relative for cheap rotation around the chunk centroid.
    const verts = new Float32Array(poly.length);
    for (let i = 0; i < vertCount; i++) {
      verts[i * 2] = (poly[i * 2] as number) - cx;
      verts[i * 2 + 1] = (poly[i * 2 + 1] as number) - cy;
    }
    const dx = cx - ix;
    const dy = cy - iy;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > maxDist) maxDist = d;
    skels.push({ verts, vertCount, cx, cy, area, distToImpact: d });
  }
  // Second pass: brightnessKey + Path2D.
  for (const s of skels) {
    const brightnessKey = maxDist > 1e-6 ? 1 - s.distToImpact / maxDist : 0.5;
    const path = new Path2D();
    path.moveTo(s.verts[0] as number, s.verts[1] as number);
    for (let i = 1; i < s.vertCount; i++) {
      path.lineTo(s.verts[i * 2] as number, s.verts[i * 2 + 1] as number);
    }
    path.closePath();
    cells.push({
      verts: s.verts,
      vertCount: s.vertCount,
      cx: s.cx,
      cy: s.cy,
      area: s.area,
      brightnessKey,
      path,
    });
  }
  const order: number[] = cells.map((_, i) => i);
  order.sort((a, b) => {
    const ca = cells[a] as Cell;
    const cb = cells[b] as Cell;
    return cb.brightnessKey - ca.brightnessKey;
  });
  const indexByBrightness = new Uint16Array(order);
  return { cells, indexByBrightness };
}

// ---- Public API -------------------------------------------------------------

let _atlas: Pattern[] | null = null;

export function getAtlas(): readonly Pattern[] {
  if (_atlas === null) {
    const a: Pattern[] = new Array<Pattern>(PATTERN_COUNT);
    for (let zone = 0; zone < ZONE_COUNT; zone++) {
      for (let intensity = 0; intensity < INTENSITY_COUNT; intensity++) {
        for (let v = 0; v < VARIANTS_PER_BUCKET; v++) {
          a[patternIndex(zone, intensity, v)] = buildPattern(zone, intensity, v);
        }
      }
    }
    _atlas = a;
  }
  return _atlas;
}

export function getPattern(zone: ImpactZone, intensity: Intensity, variant: number): Pattern {
  return (getAtlas()[patternIndex(zone, intensity, variant)]) as Pattern;
}
